import React, { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';
import { attendanceApi, AttendancePunch } from '../../services/attendanceApi';
import { fromIstLocalInput, toIstLocalInput } from './attendanceFormat';

interface Props {
  punch: AttendancePunch | null;
  staffName: string;
  studioId?: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Owner/admin correction of one punch — typically adding a forgotten
 * punch-out. Every change is kept in the audit log with the note.
 */
const EditPunchDialog: React.FC<Props> = ({ punch, staffName, studioId, onClose, onSaved }) => {
  const [inAt, setInAt] = useState('');
  const [outAt, setOutAt] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!punch) return;
    setInAt(toIstLocalInput(punch.inAt));
    setOutAt(toIstLocalInput(punch.outAt));
    setNote('');
    setError(null);
  }, [punch]);

  const save = async () => {
    if (!punch) return;
    if (!inAt) {
      setError('Punch-in time is required.');
      return;
    }
    if (outAt && outAt <= inAt) {
      setError('Punch-out must be after punch-in.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await attendanceApi.editPunch(
        punch._id,
        {
          inAt: fromIstLocalInput(inAt),
          ...(outAt ? { outAt: fromIstLocalInput(outAt) } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
        studioId,
      );
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!punch} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Correct punch · {staffName}</DialogTitle>
      <DialogContent>
        {punch?.status === 'missed_out' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No punch-out was recorded. Add the time they left so the hours count.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth type="datetime-local" label="Punch in (IST)" value={inAt}
          onChange={(e) => setInAt(e.target.value)} sx={{ mt: 1, mb: 2 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth type="datetime-local" label="Punch out (IST)" value={outAt}
          onChange={(e) => setOutAt(e.target.value)} sx={{ mb: 2 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth label="Reason" value={note}
          onChange={(e) => setNote(e.target.value)} multiline minRows={2}
          slotProps={{ htmlInput: { maxLength: 300 } }}
        />
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Corrected punches are marked as edited in the report.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPunchDialog;
